import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";

interface TeamMember {
  id: string;
  email: string;
  role: 'owner' | 'editor' | 'viewer';
}

interface TeamManagementProps {
  tenderId: string;
}

export const TeamManagement = ({ tenderId }: TeamManagementProps) => {
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState<'editor' | 'viewer'>('viewer');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation('offerManagement');

  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const { data: teamMembers = [], isLoading } = useQuery({
    queryKey: ['team-members', tenderId],
    queryFn: async () => {
      if (!isValidUUID(tenderId)) {
        console.error('Invalid tender ID format:', tenderId);
        return [];
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('tender_team_members')
        .select(`
          id,
          email,
          role,
          user_id,
          profiles:user_id (
            id,
            company_name,
            email
          )
        `)
        .eq('tender_id', tenderId);

      if (error) {
        console.error('Error fetching team members:', error);
        throw error;
      }
      return data || [];
    },
    enabled: Boolean(tenderId)
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: 'editor' | 'viewer' }) => {
      if (!isValidUUID(tenderId)) {
        throw new Error('Invalid tender ID format');
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('notification_email', email)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) {
        throw new Error(t("team.noUserFound"));
      }

      const { error: memberError } = await supabase
        .from('tender_team_members')
        .insert({
          tender_id: tenderId,
          user_id: profile.id,
          email: email,
          role: role
        });

      if (memberError) throw memberError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', tenderId] });
      setNewMemberEmail("");
      toast({
        title: t("team.inviteSent"),
        description: t("team.inviteSentDesc"),
      });
    },
    onError: (error: Error) => {
      console.error("Error adding team member:", error);
      toast({
        title: t("team.inviteError"),
        description: error.message || t("team.inviteErrorDesc"),
        variant: "destructive",
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('tender_team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', tenderId] });
      toast({
        title: t("team.memberRemoved"),
        description: t("team.memberRemovedDesc"),
      });
    },
    onError: (error) => {
      console.error("Error removing team member:", error);
      toast({
        title: t("team.removeError"),
        description: t("team.removeErrorDesc"),
        variant: "destructive",
      });
    },
  });

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemberEmail) return;

    addMemberMutation.mutate({
      email: newMemberEmail,
      role: newMemberRole
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("team.management")}</CardTitle>
        <CardDescription>
          {t("team.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleAddMember} className="flex gap-4 mb-6">
          <Input
            type="email"
            placeholder={t("team.emailPlaceholder")}
            value={newMemberEmail}
            onChange={(e) => setNewMemberEmail(e.target.value)}
            className="flex-1"
          />
          <Select
            value={newMemberRole}
            onValueChange={(value: 'editor' | 'viewer') => setNewMemberRole(value)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("team.roleSelect")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">{t("team.roles.editor")}</SelectItem>
              <SelectItem value="viewer">{t("team.roles.viewer")}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={!newMemberEmail}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t("team.sendInvitation")}
          </Button>
        </form>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("team.email")}</TableHead>
                <TableHead>{t("team.role")}</TableHead>
                <TableHead className="w-[100px]">{t("documents.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    {t("common.loading")}
                  </TableCell>
                </TableRow>
              ) : teamMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    {t("team.noMembers")}
                  </TableCell>
                </TableRow>
              ) : (
                teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.email}</TableCell>
                    <TableCell className="capitalize">{member.role}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMemberMutation.mutate(member.id)}
                        title={t("team.remove")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
